import { View, Linking, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import useAuth from "@/hooks/useAuth";
import { PermissionsType } from "@/types/db/roles";
import { useTheme } from "@/hooks/useTheme";
import useContextMenu from "@/hooks/useContextMenu";
import { useRef, useMemo, useEffect, useState } from "react";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useCall } from "@/hooks/useCall";
import { callService, CallHistoryRecord } from "@/services/callService";

export type PhoneProps = {
  type?: "cell" | "office" | "home";
  /** The destination phone number to call */
  number: string;
};

export function Phone({ number }: PhoneProps) {
  const { userRoles } = useAuth();
  const { colors } = useTheme();
  const [recentCall, setRecentCall] = useState<CallHistoryRecord | null>(null);

  const phones = useMemo(() => {
    if (!userRoles) return [];
    const allPhones = userRoles.flatMap((r) => r.permissions.phones || []);

    // Deduplicate by phone number, keeping first occurrence
    const uniqueMap = new Map();
    allPhones.forEach((phone) => {
      if (!uniqueMap.has(phone.number)) {
        uniqueMap.set(phone.number, phone);
      }
    });

    return Array.from(uniqueMap.values());
  }, [userRoles]);

  // Load recent call history for this number
  useEffect(() => {
    const loadRecentCall = async () => {
      try {
        const calls = await callService.getCallsForNumber(number, 1);
        if (calls.length > 0) {
          setRecentCall(calls[0]);
        }
      } catch (error) {
        // Silently fail - this is just for UI enhancement
        console.warn("Failed to load call history for number:", error);
      }
    };

    loadRecentCall();
  }, [number]);

  const formatRecentCallTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}d`;
  };

  return (
    <Pressable style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
      <MaterialCommunityIcons name={"phone"} size={16} color={colors.text} />
      <View style={{ flex: 1, minWidth: 120 }}>
        <ThemedText>{number}</ThemedText>
        {recentCall && (
          <ThemedText style={{ fontSize: 10, opacity: 0.5 }}>
            Last {recentCall.direction === "inbound" ? "called" : "call"} {formatRecentCallTime(recentCall.created_at)}{" "}
            ago
          </ThemedText>
        )}
      </View>
      <Menu phones={phones} destinationNumber={number} recentCall={recentCall} />
    </Pressable>
  );
}
export type MenuProps = {
  phones: PermissionsType["phones"];
  destinationNumber: string;
  recentCall?: CallHistoryRecord | null;
};

function Menu({ phones, destinationNumber, recentCall }: MenuProps) {
  const { colors } = useTheme();
  const call = useCall();
  const contextMenu = useContextMenu();
  const ref = useRef<View>(null);
  const [hasFaceTime, setHasFaceTime] = useState(false);

  // Detect FaceTime Audio availability on mount
  useEffect(() => {
    Linking.canOpenURL("facetime-audio://")
      .then(setHasFaceTime)
      .catch(() => setHasFaceTime(false));
  }, []);

  const handleTwilioCall = (fromPhone: PermissionsType["phones"][number]) => {
    call.startCall({
      callerName: fromPhone.alias || fromPhone.number,
      phoneNumber: destinationNumber,
      fromNumber: fromPhone.number,
    });
    contextMenu.setVisible(false);
  };

  const handleFaceTimeAudio = async () => {
    const url = `facetime-audio:${destinationNumber}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      contextMenu.setVisible(false);
    }
  };

  const handleDeviceDialer = async () => {
    const url = `tel:${destinationNumber}`;
    const supported = await Linking.canOpenURL(url);
    if (supported) {
      await Linking.openURL(url);
      contextMenu.setVisible(false);
    }
  };

  const menuContent = (
    <View style={[styles.menu, { backgroundColor: colors.background, borderColor: colors.border }]}>
      {phones.map((phone) => {
        // Respect show_number prop - if true, show number; if false, alias must exist
        const displayText = phone.show_number ? phone.number : phone.alias || phone.number;

        return (
          <Button
            key={phone.number}
            text={displayText}
            iconSize={18}
            icon="phone"
            style={styles.menuItems}
            onPress={() => handleTwilioCall(phone)}
          />
        );
      })}
      {phones.length > 0 && <View style={{ height: 1, backgroundColor: colors.border, marginVertical: 4 }} />}

      {/* Show FaceTime Audio if available (macOS/iOS), otherwise show generic device dialer */}
      {hasFaceTime ? (
        <Button
          text="Call with FaceTime Audio"
          iconSize={18}
          icon="video"
          style={styles.menuItems}
          onPress={handleFaceTimeAudio}
        />
      ) : (
        <Button
          text="Use Device Dialer"
          iconSize={18}
          icon="phone-classic"
          style={styles.menuItems}
          onPress={handleDeviceDialer}
        />
      )}
    </View>
  );
  return (
    <Button
      style={() => [styles.button, {}]}
      ref={ref}
      icon="dots-horizontal"
      onPress={() => {
        contextMenu.setIgnoreRef(ref);
        ref.current?.measureInWindow((pageX, pageY) => contextMenu.setLocation({ x: pageX - 80, y: pageY + 30 }));
        contextMenu.setMenuContent(menuContent);
        contextMenu.setVisible((p) => !p);
        contextMenu.setStyle({ backgroundColor: colors.background, overflow: "hidden" });
      }}
    />
  );
}
const styles = StyleSheet.create({
  button: {
    width: 25,
  },
  menu: {
    borderWidth: 0,
  },
  menuItems: {
    backgroundColor: "transparent",
    borderWidth: 0,
    justifyContent: "flex-start",
    gap: 8,
  },
});
